/**
 * @file     marketReport.ts
 * @purpose  Aggregate stats for programmatic market-report pages. All
 *           deterministic math, no LLM (ADR-009). Operates on the
 *           display-rule-filtered view so we never leak gated listings.
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import { fetchAllRows } from './pagedFetch';
import { reportSilentFailure } from '@/core/observability/reportSilentFailure';
import { coverageRatio, medianRounded, quantileRounded } from '@/utils/statistics';

export interface MarketReport {
  city: string;
  /** When this aggregate was computed by the page. */
  generatedAt: string;
  /** Latest upstream listing modification included in the aggregate. */
  sourceUpdatedAt: string | null;
  activeCount: number;
  /** Null when too few rows carry a list date to state this honestly. The DDF
   *  feed leaves listing_contract_date empty (100% of Comox, 48% of Vancouver),
   *  and a bare 0 reads as "nothing new here" rather than "not reported". */
  newThisMonth: number | null;
  /** Share of active rows carrying a parseable listing-contract date. */
  listDateCoverage: number | null;
  /** Share of active rows carrying a positive list price. */
  priceCoverage: number | null;
  medianListPrice: number | null;
  minListPrice: number | null;
  maxListPrice: number | null;
  /** Asking-price quartiles. These describe the middle half of current supply
   *  without letting one unusual listing distort the market summary. */
  p25ListPrice: number | null;
  p75ListPrice: number | null;
  byPropertyType: { type: string; count: number; medianPrice: number | null }[];
  byNeighbourhood: {
    area: string;
    slug: string;
    count: number;
    medianPrice: number | null;
  }[];
  /** `mls_area_major` values that carry a meaningful share of the city's live
   *  listings. Feeds the exact city -> region-token mapping behind the
   *  /properties links. Thin values are dropped: feeds mis-tag the odd listing
   *  (a Delta row filed under North Surrey), and one bad row should not widen
   *  a whole city's search. */
  areaMajors: string[];
}

/** Friendly labels for the normalized dwelling_category column. */
export const DWELLING_LABEL: Record<string, string> = {
  detached: 'Detached',
  condo: 'Condo / Apartment',
  townhouse: 'Townhouse',
  half_duplex: 'Half-duplex',
  duplex: 'Duplex',
  multifamily: 'Multi-family',
  land: 'Land',
  farm: 'Farm',
  commercial: 'Commercial',
  mobile: 'Mobile home',
  other: 'Other',
};

/** An `mls_area_major` must clear both bars to count as one of a city's areas. */
/** Share of rows that must carry a list date before new-supply counts are publishable. */
export const LIST_DATE_MIN_COVERAGE = 0.5;

const AREA_MAJOR_MIN_COUNT = 2;
const AREA_MAJOR_MIN_SHARE = 0.01;

function neighbourhoodSlug(area: string): string {
  return area
    .trim()
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

export async function buildMarketReport(
  db: SupabaseClient,
  city: string,
): Promise<MarketReport | null> {
  const data = await fetchAllRows<{
    list_price: number | null;
    listing_contract_date: string | null;
    dwelling_category: string | null;
    mls_area_minor: string | null;
    mls_area_major: string | null;
    city: string | null;
    modification_timestamp: string | null;
  }>(
    db,
    'v_idx_canonical_properties',
    'list_price,listing_contract_date,dwelling_category,mls_area_minor,mls_area_major,city,modification_timestamp',
    (q) => q.eq('city', city),
    'listing_key',
  );
  if (data.length === 0) return null;

  const now = Date.now();
  const monthAgo = now - 30 * 86_400_000;
  let datedRows = 0;
  let sourceUpdatedAt: string | null = null;

  const prices: number[] = [];
  let newThisMonth = 0;
  const byType = new Map<string, { count: number; prices: number[] }>();
  const byArea = new Map<string, { area: string; count: number; prices: number[] }>();
  const areaMajorCounts = new Map<string, number>();

  for (const r of data as Array<{
    list_price: number | null;
    listing_contract_date: string | null;
    dwelling_category: string | null;
    mls_area_minor: string | null;
    mls_area_major: string | null;
    modification_timestamp: string | null;
  }>) {
    if (r.modification_timestamp && (!sourceUpdatedAt || r.modification_timestamp > sourceUpdatedAt)) {
      sourceUpdatedAt = r.modification_timestamp;
    }
    if (r.mls_area_major) areaMajorCounts.set(r.mls_area_major, (areaMajorCounts.get(r.mls_area_major) ?? 0) + 1);
    if (typeof r.list_price === 'number' && r.list_price > 0) prices.push(r.list_price);
    if (r.listing_contract_date) {
      const t = Date.parse(r.listing_contract_date);
      if (!Number.isNaN(t)) {
        datedRows += 1;
        if (t >= monthAgo) newThisMonth += 1;
      }
    }
    if (r.dwelling_category) {
      const k = DWELLING_LABEL[r.dwelling_category] ?? r.dwelling_category;
      let b = byType.get(k);
      if (!b) { b = { count: 0, prices: [] }; byType.set(k, b); }
      b.count += 1;
      if (typeof r.list_price === 'number' && r.list_price > 0) b.prices.push(r.list_price);
    }
    if (r.mls_area_minor) {
      const k = r.mls_area_minor.toLowerCase();
      let b = byArea.get(k);
      if (!b) { b = { area: r.mls_area_minor, count: 0, prices: [] }; byArea.set(k, b); }
      b.count += 1;
      if (typeof r.list_price === 'number' && r.list_price > 0) b.prices.push(r.list_price);
    }
  }

  return {
    city,
    generatedAt: new Date().toISOString(),
    sourceUpdatedAt,
    activeCount: data.length,
    newThisMonth: datedRows >= data.length * LIST_DATE_MIN_COVERAGE ? newThisMonth : null,
    listDateCoverage: coverageRatio(datedRows, data.length),
    priceCoverage: coverageRatio(prices.length, data.length),
    medianListPrice: medianRounded(prices),
    minListPrice: prices.length ? Math.min(...prices) : null,
    maxListPrice: prices.length ? Math.max(...prices) : null,
    p25ListPrice: quantileRounded(prices, 0.25),
    p75ListPrice: quantileRounded(prices, 0.75),
    byPropertyType: [...byType.entries()]
      .map(([type, b]) => ({ type, count: b.count, medianPrice: medianRounded(b.prices) }))
      .sort((a, b) => b.count - a.count),
    byNeighbourhood: [...byArea.values()]
      .map((b) => ({
        area: b.area,
        slug: neighbourhoodSlug(b.area),
        count: b.count,
        medianPrice: medianRounded(b.prices),
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 20),
    areaMajors: [...areaMajorCounts.entries()]
      .filter(([, n]) => n >= AREA_MAJOR_MIN_COUNT && n >= data.length * AREA_MAJOR_MIN_SHARE)
      .sort((a, b) => b[1] - a[1])
      .map(([name]) => name),
  };
}


interface CityAggregateRow {
  city: string;
  active_count: number | string;
  median_list_price: number | string | null;
  last_modified: string | null;
}

/**
 * One aggregate per process, not per page. listMarketReportCities runs for
 * every market-report page (274 of them), the sitemap and generateStaticParams;
 * fanning that many concurrent aggregates at the displayable view is what hit
 * `canceling statement due to statement timeout` and broke production builds.
 */
const CITY_AGG_TTL_MS = 5 * 60 * 1000;
interface CityAggregateCacheState {
  cached: { at: number; rows: CityAggregateRow[] } | null;
  inFlight: Promise<CityAggregateRow[]> | null;
}

const marketReportGlobal = globalThis as typeof globalThis & {
  __reltaMarketReportCityAggregates?: CityAggregateCacheState;
};
const cityAggregateState =
  marketReportGlobal.__reltaMarketReportCityAggregates
  ?? { cached: null, inFlight: null };
marketReportGlobal.__reltaMarketReportCityAggregates = cityAggregateState;

/** Test seam: the process-level memo would otherwise leak between cases. */
export function __resetMarketReportCaches(): void {
  cityAggregateState.cached = null;
  cityAggregateState.inFlight = null;
}

async function cachedCityAggregates(db: SupabaseClient): Promise<CityAggregateRow[]> {
  const fresh =
    cityAggregateState.cached
    && Date.now() - cityAggregateState.cached.at < CITY_AGG_TTL_MS;
  if (fresh && cityAggregateState.cached) return cityAggregateState.cached.rows;
  if (cityAggregateState.inFlight) return cityAggregateState.inFlight;

  cityAggregateState.inFlight = (async () => {
    // Floor of 5, not 1: filtering happens per caller and the lowest caller
    // asks for 10, so this keeps headroom while fetching far fewer rows.
    // The aggregate itself is ~200ms; the failure mode is a transient
    // statement timeout (57014) under build-time prerender concurrency. Retry
    // once after a short backoff so a stampede degrades to a slow render, not
    // a data-less one.
    const ATTEMPTS = 2;
    for (let attempt = 0; attempt < ATTEMPTS; attempt++) {
      const { data, error } = await db.rpc('list_market_cities', { min_listings: 5 });
      if (!error) {
        const rows = (data ?? []) as CityAggregateRow[];
        cityAggregateState.cached = { at: Date.now(), rows };
        return rows;
      }
      if (attempt < ATTEMPTS - 1) {
        await new Promise((r) => setTimeout(r, 750));
        continue;
      }
      reportSilentFailure(error, { area: 'mls/listMarketCities' });
      return cityAggregateState.cached?.rows ?? [];
    }
    return cityAggregateState.cached?.rows ?? [];
  })();

  try {
    return await cityAggregateState.inFlight;
  } finally {
    cityAggregateState.inFlight = null;
  }
}

/**
 * A city slug resolved against the live list, with the failure case separated
 * from the not-found case. An empty aggregate always means the fetch failed:
 * the feed has hundreds of cities. Treating that as "city does not exist" is
 * what turned a transient timeout into a 404 on real pages.
 */
export async function resolveCityBySlug(
  db: SupabaseClient,
  slug: string,
  toSlug: (city: string) => string,
  minListings = 10,
): Promise<{ city: string; count: number; degraded: boolean } | null> {
  const cities = await listMarketReportCities(db, minListings);
  if (cities.length === 0) {
    // Lookup failed. Fall back to the slug itself so the page still renders
    // rather than 404ing a city that exists.
    const city = slug
      .split('-')
      .map((w) => (w ? w[0]!.toUpperCase() + w.slice(1) : w))
      .join(' ');
    return { city, count: 0, degraded: true };
  }
  const match = cities.find((c) => toSlug(c.city) === slug);
  return match ? { city: match.city, count: match.count, degraded: false } : null;
}

/** Distinct cities with at least N active listings (for sitemap + static-params). */
export async function listMarketReportCities(
  db: SupabaseClient,
  minListings = 10,
): Promise<{
  city: string;
  count: number;
  /** Included by the aggregate RPC. Optional keeps degraded caller fallbacks
   * compatible while data-backed rows always populate it. */
  medianPrice?: number | null;
  lastModified: string | null;
}[]> {
  const rows = await cachedCityAggregates(db);
  return rows
    .filter((r) => Number(r.active_count) >= minListings)
    .map((r) => ({
      city: r.city,
      count: Number(r.active_count),
      medianPrice:
        r.median_list_price == null || !Number.isFinite(Number(r.median_list_price))
          ? null
          : Number(r.median_list_price),
      lastModified: r.last_modified,
    }));
}

export interface ProvinceOverview {
  totalActive: number;
  medianListPrice: number | null;
  cities: {
    city: string;
    slug: string;
    count: number;
    medianPrice: number | null;
  }[];
}

/** Province-wide rollup for the /british-columbia hub. One paged pass. */
export async function buildProvinceOverview(
  db: SupabaseClient,
  minCityListings = 10,
): Promise<ProvinceOverview> {
  const [rows, totals] = await Promise.all([
    cachedCityAggregates(db),
    db.rpc('market_province_totals'),
  ]);
  const total = ((totals.data ?? [])[0] ?? {}) as {
    total_active?: number | string;
    median_list_price?: number | string | null;
  };
  const cities = rows
    .filter((r) => Number(r.active_count) >= minCityListings)
    .map((r) => ({
      city: r.city,
      slug: neighbourhoodSlug(r.city),
      count: Number(r.active_count),
      medianPrice: r.median_list_price == null ? null : Number(r.median_list_price),
    }));
  return {
    totalActive: Number(total.total_active ?? 0),
    medianListPrice: total.median_list_price == null ? null : Number(total.median_list_price),
    cities,
  };
}
